import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  CornerUpLeft,
  FileEdit,
  MoreHorizontal,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  MEASURE_UNITS,
  MEASURE_UNIT_LABELS,
  REQUISITION_APPROVALS,
  REQUISITION_APPROVAL_LABELS,
  REQUISITION_FULFILMENTS,
  REQUISITION_FULFILMENT_LABELS,
  REQUISITION_ITEM_KINDS,
  REQUISITION_ITEM_KIND_LABELS,
  REQUISITION_PRIORITIES,
  REQUISITION_PRIORITY_LABELS,
  formatCents,
  formatQty,
  type MeasureUnit,
  type Paginated,
  type Requisition,
  type RequisitionApproval,
  type RequisitionItemKind,
  type RequisitionPriority,
  type RequisitionSummary,
  type RequisitionTimelineEntry,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate, formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput, QtyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, LoadingState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useCostCenters, useDepartments, useItems, useUnits, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

const APPROVAL_TONE: Record<RequisitionApproval, BadgeKind> = {
  RASCUNHO: 'mute',
  AGUARDANDO_APROVACAO: 'warn',
  APROVADA: 'ok',
  DEVOLVIDA: 'warn',
  REPROVADA: 'bad',
  CANCELADA: 'mute',
};

interface ItemDraft {
  itemId: string;
  kind: RequisitionItemKind;
  description: string;
  unit: MeasureUnit;
  quantityMilli: number | null;
  estimatedUnitCents: number | null;
  /**
   * A observação da linha não tem campo no formulário, mas ANDA JUNTO.
   *
   * A gravação apaga e recria todas as linhas: sem carregar isto de volta,
   * salvar uma correção de quantidade apagava em silêncio a observação que
   * alguém tinha escrito — inclusive a que veio pela API.
   */
  notes: string | null;
}

const LINHA_VAZIA: ItemDraft = {
  itemId: '',
  kind: 'MATERIAL',
  description: '',
  unit: 'UN',
  quantityMilli: null,
  estimatedUnitCents: null,
  notes: null,
};

/**
 * As seis ações do fluxo são a mesma chamada com outro caminho.
 *
 * Um hook nomeado em vez de uma função auxiliar dentro do componente: as
 * regras dos hooks valem, e é o que o React exige para a ordem das chamadas
 * ser sempre a mesma entre renders.
 */
function useAcao(id: string | null, caminho: string, onSuccess: () => void) {
  return useMutation({
    mutationFn: (body: unknown) => api.post(`/purchase-requisitions/${id}/${caminho}`, body ?? {}),
    onSuccess,
  });
}

/**
 * Requisições de compra — o começo da cadeia.
 *
 * A obra pede, alguém aprova, a compra cota. Duas regras que valem no
 * servidor e são repetidas aqui só para o operador saber antes de clicar:
 * quem pede não aprova, e o valor precisa caber na alçada de quem aprova.
 */
export function RequisicoesPage() {
  const { can, user } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [approval, setApproval] = useState('');
  const [fulfilment, setFulfilment] = useState('');
  const [priority, setPriority] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [checked, setChecked] = useState<string[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editando, setEditando] = useState<Requisition | null>(null);
  const [criando, setCriando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [reprovando, setReprovando] = useState(false);
  const [devolvendo, setDevolvendo] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [excluindo, setExcluindo] = useState<Requisition | null>(null);

  // Um objeto só: os cartões e a lista PRECISAM ler o mesmo recorte, senão o
  // número do topo não explica a tabela de baixo.
  const filtros = {
    search: search || undefined,
    approval: approval || undefined,
    fulfilment: fulfilment || undefined,
    priority: priority || undefined,
    costCenterId: costCenterId || undefined,
    departmentId: departmentId || undefined,
    unitId: unitId || undefined,
    from: from || undefined,
    to: to || undefined,
  };

  const listQuery = useQuery({
    queryKey: ['purchase-requisitions', { ...filtros, page, perPage }],
    queryFn: () => api.get<Paginated<Requisition>>('/purchase-requisitions', { ...filtros, page, perPage }),
  });

  const summaryQuery = useQuery({
    queryKey: ['purchase-requisitions', 'summary', filtros],
    queryFn: () => api.get<RequisitionSummary>('/purchase-requisitions/summary', filtros),
  });

  const detailQuery = useQuery({
    queryKey: ['purchase-requisition', selectedId],
    queryFn: () => api.get<Requisition>(`/purchase-requisitions/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-requisition'] });
  };

  const submitMutation = useAcao(selectedId, 'submit', refresh);
  const approveMutation = useAcao(selectedId, 'approve', refresh);
  const rejectMutation = useAcao(selectedId, 'reject', refresh);
  const returnMutation = useAcao(selectedId, 'return', refresh);
  const cancelMutation = useAcao(selectedId, 'cancel', refresh);
  const reopenMutation = useAcao(selectedId, 'reopen', refresh);

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/purchase-requisitions/${id}`),
    onSuccess: () => {
      setExcluindo(null);
      refresh();
    },
  });

  const requisition = detailQuery.data;
  const souOSolicitante = requisition?.requesterId === user?.id;

  const maisFiltrosAtivos = [costCenterId, departmentId, unitId, from, to].filter(Boolean).length;
  const temFiltro = Boolean(search || approval || fulfilment || priority) || maisFiltrosAtivos > 0;

  const limparMais = () => {
    setCostCenterId('');
    setDepartmentId('');
    setUnitId('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const limparTudo = () => {
    setSearch('');
    setApproval('');
    setFulfilment('');
    setPriority('');
    limparMais();
  };

  const resumo = summaryQuery.data;

  /**
   * As contagens viraram ABAS em vez de cartões.
   *
   * Três dos cinco cartões já FILTRAVAM ao serem clicados — eram aba com
   * cara de cartão, ocupando 110px do alto da tela para dizer um número de
   * dois dígitos. Como aba, o mesmo número cabe numa fita de 40px e diz na
   * hora qual recorte está no ar.
   *
   * Os dois que NÃO filtram ficaram de fora: "Em andamento" cobre três
   * situações (em cotação, com pedido, parcial) e a API filtra por uma só;
   * "Estimado pendente" é dinheiro, não recorte. Aba que não filtra é
   * mentira — esses dois viram texto ao lado da fita.
   */
  const abas = [
    {
      chave: 'aguardando',
      rotulo: 'Aguardando aprovação',
      conta: resumo?.waitingApproval ?? 0,
      ativa: approval === 'AGUARDANDO_APROVACAO',
      escolher: () => {
        setApproval(approval === 'AGUARDANDO_APROVACAO' ? '' : 'AGUARDANDO_APROVACAO');
        setFulfilment('');
        setPage(1);
      },
    },
    {
      chave: 'aprovadas',
      rotulo: 'Aprovadas na fila',
      conta: resumo?.approvedPending ?? 0,
      ativa: approval === 'APROVADA' && fulfilment === 'NAO_INICIADO',
      escolher: () => {
        const ligado = approval === 'APROVADA' && fulfilment === 'NAO_INICIADO';
        setApproval(ligado ? '' : 'APROVADA');
        setFulfilment(ligado ? '' : 'NAO_INICIADO');
        setPage(1);
      },
    },
    {
      chave: 'atendidas',
      rotulo: 'Atendidas',
      conta: resumo?.attended ?? 0,
      ativa: fulfilment === 'ATENDIDA' && !approval,
      escolher: () => {
        setApproval('');
        setFulfilment(fulfilment === 'ATENDIDA' ? '' : 'ATENDIDA');
        setPage(1);
      },
    },
  ];
  const nenhumaAbaAtiva = !abas.some((aba) => aba.ativa);


  const columns: Array<Column<Requisition>> = [
    {
      key: 'code',
      label: 'Código',
      mono: true,
      strong: true,
      sortable: true,
      render: (row) => row.code,
    },
    {
      key: 'createdAt',
      label: 'Data',
      mono: true,
      sortable: true,
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'requesterName',
      label: 'Solicitante',
      render: (row) => (
        <span>
          {blank(row.requesterName)}
          {row.departmentName && (
            <span style={{ display: 'block', fontSize: 11, color: T.inkFaint }}>
              {row.departmentName}
            </span>
          )}
        </span>
      ),
    },
    { key: 'costCenterName', label: 'Centro de custo', render: (row) => blank(row.costCenterName) },
    {
      key: 'items',
      label: 'Itens',
      right: true,
      mono: true,
      render: (row) => (
        <span title={row.items.map((item) => item.description).join(', ')}>
          {row.items.length}
          {row.pendingItemCount > 0 && row.pendingItemCount < row.items.length && (
            <span style={{ color: T.inkFaint }}> ({row.pendingItemCount} pend.)</span>
          )}
        </span>
      ),
    },
    {
      key: 'neededBy',
      label: 'Precisa até',
      mono: true,
      sortable: true,
      render: (row) => (row.neededBy ? formatDate(row.neededBy) : '—'),
    },
    {
      key: 'priority',
      label: 'Prioridade',
      render: (row) =>
        row.priority === 'NORMAL' ? (
          <span style={{ color: T.inkFaint }}>Normal</span>
        ) : (
          <StatusBadge kind={row.priority === 'EMERGENCIAL' ? 'bad' : 'warn'}>
            {REQUISITION_PRIORITY_LABELS[row.priority]}
          </StatusBadge>
        ),
    },
    {
      key: 'approval',
      label: 'Aprovação',
      render: (row) => (
        <StatusBadge kind={APPROVAL_TONE[row.approval]}>
          {REQUISITION_APPROVAL_LABELS[row.approval]}
        </StatusBadge>
      ),
    },
    {
      key: 'fulfilment',
      label: 'Andamento',
      render: (row) =>
        row.fulfilment === 'NAO_INICIADO' ? (
          <span style={{ color: T.inkFaint }}>—</span>
        ) : (
          <StatusBadge kind={row.fulfilment === 'ATENDIDA' ? 'ok' : 'info'}>
            {REQUISITION_FULFILMENT_LABELS[row.fulfilment]}
          </StatusBadge>
        ),
    },
    {
      key: 'estimatedTotalCents',
      label: 'Estimado',
      right: true,
      mono: true,
      sortable: true,
      render: (row) => formatCents(row.estimatedTotalCents),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Requisições"
        actions={
          can('requisicoes:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCriando(true)}>
              Nova requisição
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {/* O título "Fila da operação" saiu: repetia o "Requisições" que já
            está no cabeçalho, duas linhas acima. */}
        <div className="fita">
          <div className="abas" role="tablist" aria-label="Situação da requisição">
            <button
              type="button"
              role="tab"
              aria-selected={nenhumaAbaAtiva}
              className={`aba${nenhumaAbaAtiva ? ' is-active' : ''}`}
              onClick={() => {
                setApproval('');
                setFulfilment('');
                setPage(1);
              }}
            >
              Todas <span className="aba__conta">{listQuery.data?.meta.total ?? 0}</span>
            </button>
            {abas.map((aba) => (
              <button
                key={aba.chave}
                type="button"
                role="tab"
                aria-selected={aba.ativa}
                className={`aba${aba.ativa ? ' is-active' : ''}`}
                onClick={aba.escolher}
              >
                {aba.rotulo} <span className="aba__conta">{aba.conta}</span>
              </button>
            ))}
          </div>

          {/* Os dois números que não viram recorte. Ficam à direita, em texto:
              informam sem fingir que são botão. */}
          <div className="fita__notas">
            <span>
              Em andamento <strong>{resumo?.inProgress ?? 0}</strong>
            </span>
            <span>
              Estimado pendente{' '}
              <strong className="mono">{formatCents(resumo?.estimatedPendingCents ?? 0)}</strong>
            </span>
            {temFiltro && (
              <button type="button" className="purchase-list-heading__clear" onClick={limparTudo}>
                Limpar filtros
              </button>
            )}
          </div>
        </div>

        <FilterBar
          placeholder="Buscar por código, item ou justificativa…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'approval',
              label: 'Aprovação',
              value: approval,
              options: REQUISITION_APPROVALS.map((value) => ({
                value,
                label: REQUISITION_APPROVAL_LABELS[value],
              })),
              onChange: (value) => {
                setApproval(value);
                setPage(1);
              },
            },
            {
              key: 'fulfilment',
              label: 'Andamento',
              value: fulfilment,
              options: REQUISITION_FULFILMENTS.map((value) => ({
                value,
                label: REQUISITION_FULFILMENT_LABELS[value],
              })),
              onChange: (value) => {
                setFulfilment(value);
                setPage(1);
              },
            },
            {
              key: 'priority',
              label: 'Prioridade',
              value: priority,
              options: REQUISITION_PRIORITIES.map((value) => ({
                value,
                label: REQUISITION_PRIORITY_LABELS[value],
              })),
              onChange: (value) => {
                setPriority(value);
                setPage(1);
              },
            },
          ]}
          activeMoreFilters={maisFiltrosAtivos}
          onClearMore={limparMais}
          moreFilters={
            <MaisFiltros
              costCenterId={costCenterId}
              departmentId={departmentId}
              unitId={unitId}
              from={from}
              to={to}
              onChange={(patch) => {
                if (patch.costCenterId !== undefined) setCostCenterId(patch.costCenterId);
                if (patch.departmentId !== undefined) setDepartmentId(patch.departmentId);
                if (patch.unitId !== undefined) setUnitId(patch.unitId);
                if (patch.from !== undefined) setFrom(patch.from);
                if (patch.to !== undefined) setTo(patch.to);
                setPage(1);
              }}
            />
          }
        />

        {listQuery.isPending && <TableSkeleton columns={10} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Requisições de compra"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
            checkedIds={checked}
            onCheckedChange={setChecked}
            rowActions={(row) => (
              <button
                type="button"
                className="rowaction"
                aria-label={`Abrir ${row.code}`}
                onClick={() => setSelectedId(row.id)}
              >
                <MoreHorizontal size={15} aria-hidden />
              </button>
            )}
            emptyState={
              temFiltro ? (
                <NoResultsState onClear={limparTudo} />
              ) : (
                <EmptyState
                  title="Nenhuma requisição ainda"
                  description="Quem precisa pede aqui; a compra cota depois da aprovação."
                  action={
                    can('requisicoes:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCriando(true)}>
                        Nova requisição
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={requisition?.code ?? 'Requisição'}
        subtitle={requisition ? blank(requisition.costCenterName) : undefined}
        tabs={['Resumo', 'Itens', 'Anexos', 'Histórico']}
        footer={
          requisition && (
            <Acoes
              requisition={requisition}
              souOSolicitante={souOSolicitante}
              can={can}
              onEditar={() => setEditando(requisition)}
              onEnviar={() => setEnviando(true)}
              onAprovar={() => setAprovando(true)}
              onReprovar={() => setReprovando(true)}
              onDevolver={() => setDevolvendo(true)}
              onCancelar={() => setCancelando(true)}
              onReabrir={() => reopenMutation.mutate(undefined)}
              onExcluir={() => setExcluindo(requisition)}
            />
          )
        }
      >
        {(aba) => {
          if (detailQuery.isPending) return <LoadingState label="Abrindo a requisição…" />;
          if (!requisition) return null;

          if (aba === 1) return <AbaItens requisition={requisition} />;
          if (aba === 2)
            return (
              <Attachments
                entity="PurchaseRequisition"
                entityId={requisition.id}
                canEdit={can('requisicoes:editar')}
              />
            );
          if (aba === 3) return <AbaHistorico requisitionId={requisition.id} />;
          return <AbaResumo requisition={requisition} />;
        }}
      </ContextPanel>

      <RequisitionFormDrawer
        open={criando || Boolean(editando)}
        editando={editando}
        onClose={() => {
          setCriando(false);
          setEditando(null);
        }}
        onSaved={() => {
          setCriando(false);
          setEditando(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={enviando}
        loading={submitMutation.isPending}
        title="Enviar para aprovação?"
        confirmLabel="Enviar"
        message={
          <>
            A partir daqui a requisição CONGELA: só volta a ser editável se quem
            aprova devolver para correção.
            {submitMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(submitMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setEnviando(false);
          submitMutation.reset();
        }}
        onConfirm={() => submitMutation.mutate(undefined, { onSuccess: () => setEnviando(false) })}
      />

      <ConfirmDialog
        open={aprovando}
        loading={approveMutation.isPending}
        title="Aprovar esta requisição?"
        confirmLabel="Aprovar"
        message={
          <>
            {requisition && (
              <>
                <strong>{requisition.code}</strong> — {formatCents(requisition.estimatedTotalCents)}
              </>
            )}
            <div style={{ color: T.inkSoft, marginTop: 8 }}>
              A aprovação fica registrada com o seu nome. Se o valor passar da sua
              alçada, o servidor recusa e a requisição segue para quem aprova nesta
              faixa.
            </div>
            {approveMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(approveMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setAprovando(false);
          approveMutation.reset();
        }}
        onConfirm={() =>
          approveMutation.mutate(undefined, { onSuccess: () => setAprovando(false) })
        }
      />

      <ConfirmDialog
        open={reprovando}
        destructive
        requireReason
        loading={rejectMutation.isPending}
        title="Reprovar esta requisição?"
        confirmLabel="Reprovar"
        message="Reprovada, ela não segue. Se o caso é só corrigir, use Devolver."
        onClose={() => {
          setReprovando(false);
          rejectMutation.reset();
        }}
        onConfirm={(reason) =>
          rejectMutation.mutate({ reason: reason ?? '' }, { onSuccess: () => setReprovando(false) })
        }
      />

      <ConfirmDialog
        open={devolvendo}
        requireReason
        loading={returnMutation.isPending}
        title="Devolver para correção?"
        confirmLabel="Devolver"
        message="Volta editável para o solicitante, que corrige e reenvia. O pedido original não se perde."
        onClose={() => {
          setDevolvendo(false);
          returnMutation.reset();
        }}
        onConfirm={(reason) =>
          returnMutation.mutate({ reason: reason ?? '' }, { onSuccess: () => setDevolvendo(false) })
        }
      />

      <ConfirmDialog
        open={cancelando}
        destructive
        requireReason
        loading={cancelMutation.isPending}
        title="Cancelar esta requisição?"
        confirmLabel="Cancelar requisição"
        message="Ela não se apaga: fica cancelada, com o motivo, o seu nome e a data."
        onClose={() => {
          setCancelando(false);
          cancelMutation.reset();
        }}
        onConfirm={(reason) =>
          cancelMutation.mutate({ reason: reason ?? '' }, { onSuccess: () => setCancelando(false) })
        }
      />

      <ConfirmDialog
        open={Boolean(excluindo)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este rascunho?"
        confirmLabel="Excluir"
        message={
          <>
            {excluindo?.code} nunca saiu para aprovação, então some de vez.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setExcluindo(null);
          removeMutation.reset();
        }}
        onConfirm={() => excluindo && removeMutation.mutate(excluindo.id)}
      />
    </>
  );
}

/* ------------------------------- filtros ---------------------------------- */

function MaisFiltros({
  costCenterId,
  departmentId,
  unitId,
  from,
  to,
  onChange,
}: {
  costCenterId: string;
  departmentId: string;
  unitId: string;
  from: string;
  to: string;
  onChange: (patch: Partial<Record<'costCenterId' | 'departmentId' | 'unitId' | 'from' | 'to', string>>) => void;
}) {
  const costCenters = useCostCenters();
  const departments = useDepartments();
  const units = useUnits();

  return (
    <>
      <label className="sr-only-wrap">
        <span className="sr-only">Centro de custo</span>
        <select
          className="filterbar__select"
          value={costCenterId}
          onChange={(event) => onChange({ costCenterId: event.target.value })}
        >
          <option value="">Centro de custo: todos</option>
          {(costCenters.data ?? []).map((center) => (
            <option key={center.id} value={center.id}>
              {center.code} · {center.name}
            </option>
          ))}
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Setor</span>
        <select
          className="filterbar__select"
          value={departmentId}
          onChange={(event) => onChange({ departmentId: event.target.value })}
        >
          <option value="">Setor: todos</option>
          {(departments.data ?? []).map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Unidade</span>
        <select
          className="filterbar__select"
          value={unitId}
          onChange={(event) => onChange({ unitId: event.target.value })}
        >
          <option value="">Unidade: todas</option>
          {(units.data ?? []).map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.code} · {unit.name}
            </option>
          ))}
        </select>
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Aberta a partir de</span>
        <DateInput
          className="filterbar__select"
          value={from}
          onChange={(iso) => onChange({ from: iso })}
          aria-label="Aberta a partir de"
        />
      </label>

      <label className="sr-only-wrap">
        <span className="sr-only">Aberta até</span>
        <DateInput
          className="filterbar__select"
          value={to}
          onChange={(iso) => onChange({ to: iso })}
          aria-label="Aberta até"
        />
      </label>
    </>
  );
}

/* ---------------------------- painel de contexto -------------------------- */

function AbaResumo({ requisition }: { requisition: Requisition }) {
  return (
    <>
      <StatTiles
        items={[
          { label: 'Estimado', value: formatCents(requisition.estimatedTotalCents) },
          { label: 'Itens', value: requisition.items.length },
          { label: 'Pendentes', value: requisition.pendingItemCount },
        ]}
      />

      <ReadField
        label="Aprovação"
        value={
          <StatusBadge kind={APPROVAL_TONE[requisition.approval]}>
            {REQUISITION_APPROVAL_LABELS[requisition.approval]}
          </StatusBadge>
        }
      />
      <ReadField
        label="Andamento"
        value={REQUISITION_FULFILMENT_LABELS[requisition.fulfilment]}
      />
      <ReadField label="Prioridade" value={REQUISITION_PRIORITY_LABELS[requisition.priority]} />
      <ReadField label="Solicitante" value={blank(requisition.requesterName)} />
      <ReadField label="Setor" value={blank(requisition.departmentName)} />
      <ReadField label="Centro de custo" value={blank(requisition.costCenterName)} />
      <ReadField
        label="Precisa até"
        value={requisition.neededBy ? formatDate(requisition.neededBy) : '—'}
        mono
      />
      <ReadField label="Justificativa" value={blank(requisition.justification)} />

      {requisition.approvedAt && (
        <ReadField
          label="Aprovada por"
          value={
            <>
              {blank(requisition.approverName)} · {formatDateTime(requisition.approvedAt)}
              {requisition.approvedUpToCents !== null && (
                <span style={{ display: 'block', fontSize: 11.5, color: T.inkFaint }}>
                  alçada até {formatCents(requisition.approvedUpToCents)}
                </span>
              )}
            </>
          }
        />
      )}
      {requisition.returnReason && (
        <ReadField label="Devolvida porque" value={requisition.returnReason} />
      )}
      {requisition.rejectionReason && (
        <ReadField label="Reprovada porque" value={requisition.rejectionReason} />
      )}
      {requisition.cancelReason && (
        <ReadField label="Cancelada porque" value={requisition.cancelReason} />
      )}
    </>
  );
}

/** Itens com o saldo de cada linha: pedido, em cotação, com pedido, atendido. */
function AbaItens({ requisition }: { requisition: Requisition }) {
  return (
    <div className="table-scroll">
      <table className="table">
        <caption className="sr-only">Itens da requisição</caption>
        <thead>
          <tr>
            <th>Item</th>
            <th className="is-right">Pedido</th>
            <th className="is-right">Pendente</th>
            <th className="is-right">Estimado</th>
          </tr>
        </thead>
        <tbody>
          {requisition.items.map((item) => (
            <tr key={item.id}>
              <td className="is-strong">
                {item.description}
                <span style={{ display: 'block', fontSize: 11, color: T.inkFaint }}>
                  {REQUISITION_ITEM_KIND_LABELS[item.kind]}
                  {item.sku && ` · ${item.sku}`}
                </span>
              </td>
              <td className="is-right mono">
                {formatQty(item.quantityMilli)} {item.unit}
              </td>
              <td className="is-right mono">
                {item.pendingMilli === 0 ? (
                  <span style={{ color: T.green }}>—</span>
                ) : (
                  formatQty(item.pendingMilli)
                )}
                {item.quotingMilli > 0 && (
                  <span style={{ display: 'block', fontSize: 11, color: T.inkFaint }}>
                    {formatQty(item.quotingMilli)} em cotação
                  </span>
                )}
                {item.orderedMilli > 0 && (
                  <span style={{ display: 'block', fontSize: 11, color: T.inkFaint }}>
                    {formatQty(item.orderedMilli)} com pedido
                  </span>
                )}
                {item.attendedMilli > 0 && (
                  <span style={{ display: 'block', fontSize: 11, color: T.green }}>
                    {formatQty(item.attendedMilli)} atendido
                  </span>
                )}
              </td>
              <td className="is-right mono">{formatCents(item.estimatedTotalCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACAO_LABEL: Record<string, string> = {
  CREATE: 'Criada',
  UPDATE: 'Alterada',
  APPROVE: 'Aprovada',
  REJECT: 'Reprovada',
  DELETE: 'Cancelada',
};

function AbaHistorico({ requisitionId }: { requisitionId: string }) {
  const query = useQuery({
    queryKey: ['purchase-requisition', requisitionId, 'timeline'],
    queryFn: () =>
      api.get<RequisitionTimelineEntry[]>(`/purchase-requisitions/${requisitionId}/timeline`),
  });

  if (query.isPending) return <LoadingState label="Buscando o histórico…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} />;

  const entradas = query.data ?? [];
  if (entradas.length === 0) {
    return <p className="muted" style={{ fontSize: 13 }}>Nada registrado ainda.</p>;
  }

  return (
    <ol className="timeline">
      {entradas.map((entrada) => (
        <li key={entrada.id} className="timeline__item">
          <div className="timeline__head">
            <strong>{ACAO_LABEL[entrada.action] ?? entrada.action}</strong>
            <span className="mono">{formatDateTime(entrada.at)}</span>
          </div>
          <div className="timeline__by">{blank(entrada.userName)}</div>
          {entrada.reason && <div className="timeline__reason">“{entrada.reason}”</div>}
        </li>
      ))}
    </ol>
  );
}

/* --------------------------------- ações ---------------------------------- */

function Acoes({
  requisition,
  souOSolicitante,
  can,
  onEditar,
  onEnviar,
  onAprovar,
  onReprovar,
  onDevolver,
  onCancelar,
  onReabrir,
  onExcluir,
}: {
  requisition: Requisition;
  souOSolicitante: boolean;
  can: (permission: string) => boolean;
  onEditar: () => void;
  onEnviar: () => void;
  onAprovar: () => void;
  onReprovar: () => void;
  onDevolver: () => void;
  onCancelar: () => void;
  onReabrir: () => void;
  onExcluir: () => void;
}) {
  const editavel = requisition.approval === 'RASCUNHO' || requisition.approval === 'DEVOLVIDA';
  const naMesa = requisition.approval === 'AGUARDANDO_APROVACAO';
  const encerrada = requisition.approval === 'CANCELADA';

  return (
    <>
      {editavel && can('requisicoes:editar') && (
        <Button small icon={FileEdit} onClick={onEditar}>
          Editar
        </Button>
      )}
      {editavel && can('requisicoes:editar') && (
        <Button small variant="primary" icon={Send} onClick={onEnviar}>
          Enviar para aprovação
        </Button>
      )}

      {naMesa && can('requisicoes:aprovar') && (
        <>
          {/* Quem pede não aprova. O servidor recusa de qualquer jeito; esconder
              o botão evita o clique que só devolveria erro. */}
          {!souOSolicitante && (
            <Button small variant="primary" icon={Check} onClick={onAprovar}>
              Aprovar
            </Button>
          )}
          <Button small icon={CornerUpLeft} onClick={onDevolver}>
            Devolver
          </Button>
          <Button small variant="danger" icon={X} onClick={onReprovar}>
            Reprovar
          </Button>
        </>
      )}

      {(requisition.approval === 'REPROVADA' || requisition.approval === 'DEVOLVIDA') &&
        can('requisicoes:editar') && (
          <Button small icon={CornerUpLeft} onClick={onReabrir}>
            Voltar para rascunho
          </Button>
        )}

      {requisition.approval === 'RASCUNHO' && can('requisicoes:excluir') && (
        <Button small variant="ghost" icon={Trash2} onClick={onExcluir}>
          Excluir
        </Button>
      )}

      {!encerrada && requisition.approval !== 'RASCUNHO' && can('requisicoes:excluir') && (
        <Button small variant="danger" icon={Ban} onClick={onCancelar}>
          Cancelar
        </Button>
      )}
    </>
  );
}

/* -------------------------------- formulário ------------------------------ */

/**
 * As colunas da linha de item, em UM lugar.
 *
 * O cabeçalho e a linha leem a mesma constante: se ficassem duplicados, a
 * primeira coluna acrescentada num deles desalinharia a tabela inteira e
 * ninguém veria até abrir a tela.
 *
 * A descrição é a única que estica (`2fr`): é ela que carrega "Cimento CP-II
 * 50kg saco" e é por ela que se reconhece a linha.
 */
const COLUNAS_DO_ITEM = '112px 1.4fr 2fr 116px 84px 128px 108px 40px';

function RequisitionFormDrawer({
  open,
  editando,
  onClose,
  onSaved,
}: {
  open: boolean;
  editando: Requisition | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { can } = useAuth();
  const costCenters = useCostCenters();
  const departments = useDepartments();
  const items = useItems();
  /** O envio usa a MESMA permissão da ação na lista. */
  const podeEnviar = can('requisicoes:editar');

  const [costCenterId, setCostCenterId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [priority, setPriority] = useState<RequisitionPriority>('NORMAL');
  const [neededBy, setNeededBy] = useState('');
  const [justification, setJustification] = useState('');
  const [lines, setLines] = useState<ItemDraft[]>([LINHA_VAZIA]);
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);
  // Campos que o formulário não mostra e a gravação sobrescreveria com nulo.
  const [notes, setNotes] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);

  // Carrega o registro em edição uma vez só. Ajuste durante o render em vez de
  // efeito: com efeito, o primeiro quadro mostrava o formulário vazio.
  const chave = open ? (editando?.id ?? 'novo') : null;
  if (chave !== carregadoDe) {
    setCarregadoDe(chave);
    if (open) {
      setCostCenterId(editando?.costCenterId ?? '');
      setDepartmentId(editando?.departmentId ?? '');
      setPriority(editando?.priority ?? 'NORMAL');
      setNeededBy(editando?.neededBy ?? '');
      setJustification(editando?.justification ?? '');
      setNotes(editando?.notes ?? null);
      setUnitId(editando?.unitId ?? null);
      setLines(
        editando
          ? editando.items.map((item) => ({
              itemId: item.itemId ?? '',
              kind: item.kind,
              description: item.description,
              unit: item.unit,
              quantityMilli: item.quantityMilli,
              estimatedUnitCents: item.estimatedUnitCents,
              notes: item.notes,
            }))
          : [LINHA_VAZIA],
      );
    }
  }

  /**
   * Gravar e, quando pedido, ENVIAR na mesma ação.
   *
   * Criar rascunho e depois ir na lista clicar em "Enviar para aprovação" são
   * dois passos para uma decisão só — quem abre esta tela já sabe se a
   * requisição vai ou fica. As duas chamadas continuam separadas na API: o
   * envio tem regra e auditoria próprias, e não vira efeito colateral do
   * gravar.
   *
   * Se o envio falhar, o RASCUNHO fica. É o estado verdadeiro: a requisição
   * existe e não foi enviada, e o operador tenta o envio de novo pela lista
   * sem redigitar nada.
   */
  const mutation = useMutation({
    mutationFn: async ({ body, enviar }: { body: unknown; enviar: boolean }) => {
      const salva = editando
        ? await api.put<Requisition>(`/purchase-requisitions/${editando.id}`, body)
        : await api.post<Requisition>('/purchase-requisitions', body);
      if (enviar) await api.post(`/purchase-requisitions/${salva.id}/submit`);
    },
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  const patchLine = (index: number, patch: Partial<ItemDraft>) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const total = lines.reduce(
    (acc, line) => acc + Math.round(((line.quantityMilli ?? 0) * (line.estimatedUnitCents ?? 0)) / 1000),
    0,
  );

  const validLines = lines.filter(
    (line) => line.description.trim().length >= 2 && (line.quantityMilli ?? 0) > 0,
  );
  const emergencialSemMotivo =
    priority === 'EMERGENCIAL' && justification.trim().length < 15;
  const canSubmit = Boolean(costCenterId) && validLines.length > 0 && !emergencialSemMotivo;

  /** Qual dos dois botões está em voo, para o "carregando" cair no certo. */
  const [enviando, setEnviando] = useState(false);

  const gravar = (enviar: boolean) => {
    setEnviando(enviar);
    mutation.mutate({
      enviar,
      body: {
        costCenterId,
        departmentId: departmentId || null,
        unitId,
        priority,
        neededBy: neededBy || null,
        justification: justification.trim() || null,
        notes,
        items: validLines.map((line) => ({
          itemId: line.kind === 'SERVICO' ? null : line.itemId || null,
          kind: line.kind,
          description: line.description.trim(),
          unit: line.unit,
          quantityMilli: line.quantityMilli,
          estimatedUnitCents: line.estimatedUnitCents ?? 0,
          notes: line.notes,
        })),
      },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editando ? `Editar ${editando.code}` : 'Nova requisição'}
      description="Descreva o que é preciso comprar — o preço aqui é só estimativa"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="outline"
            disabled={!canSubmit}
            loading={mutation.isPending && !enviando}
            onClick={() => gravar(false)}
          >
            {editando ? 'Salvar' : 'Salvar rascunho'}
          </Button>
          {/* Enviar é PERMISSÃO à parte: quem só cria não empurra para a mesa
              de quem aprova. É a mesma trava da lista, aqui na origem. */}
          {podeEnviar && (
            <Button
              variant="primary"
              icon={Send}
              disabled={!canSubmit}
              loading={mutation.isPending && enviando}
              onClick={() => gravar(true)}
            >
              Enviar para aprovação
            </Button>
          )}
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection title="Solicitação">
        {/* Os quatro campos curtos numa fila só; a justificativa, que é texto
            corrido, fica inteira embaixo. */}
        <div className="campos">
          <Field label="Centro de custo" htmlFor="req-cc" error={errors.costCenterId}>
          <select
            id="req-cc"
            className="input"
            value={costCenterId}
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </Field>

          <Field label="Setor solicitante" htmlFor="req-dep" error={errors.departmentId}>
            <select
              id="req-dep"
              className="input"
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
            >
              <option value="">Não informar</option>
              {(departments.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Prioridade" htmlFor="req-prio">
            <select
              id="req-prio"
              className="input"
              value={priority}
              onChange={(event) => setPriority(event.target.value as RequisitionPriority)}
            >
              {REQUISITION_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {REQUISITION_PRIORITY_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Precisa até" htmlFor="req-needed" hint="Ajuda a compra a priorizar a cotação">
            <DateInput
              id="req-needed"
              className="input input--mono"
              min={todayISO()}
              value={neededBy}
              onChange={(iso) => setNeededBy(iso)}
            />
          </Field>
        </div>

        <Field
          label="Justificativa"
          htmlFor="req-just"
          error={errors.justification}
          hint={
            priority === 'EMERGENCIAL'
              ? 'Obrigatória na emergencial: diga o que para se isto não for comprado'
              : undefined
          }
        >
          <textarea
            id="req-just"
            className="input"
            rows={2}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            placeholder="Concretagem da laje da administração na semana que vem"
          />
        </Field>
      </FormSection>

      <FormSection
        title="Itens"
        description="Item cadastrado preenche descrição e unidade sozinho"
      >
        {/*
          Grade de colunas repetida no cabeçalho e na linha: uma constante,
          para as duas NUNCA saírem de alinhamento numa edição futura.
        */}
        <div className="linhas">
          <div className="linhas__cab" style={{ gridTemplateColumns: COLUNAS_DO_ITEM }}>
            <span>Tipo</span>
            <span>Item do estoque</span>
            <span>Descrição</span>
            <span className="linhas__num">Qtde.</span>
            <span>Un.</span>
            <span className="linhas__num">Preço estimado</span>
            <span className="linhas__num">Total</span>
            <span />
          </div>

          {lines.map((line, index) => {
            const totalDaLinha = Math.round(
              ((line.quantityMilli ?? 0) * (line.estimatedUnitCents ?? 0)) / 1000,
            );
            return (
              <div
                key={index}
                className="linhas__linha"
                style={{ gridTemplateColumns: COLUNAS_DO_ITEM }}
              >
                <div className="linhas__celula" data-rotulo="Tipo">
                  <select
                    className="input"
                    aria-label={`Tipo do item ${index + 1}`}
                    value={line.kind}
                    onChange={(event) => {
                      const kind = event.target.value as RequisitionItemKind;
                      // Trocar para serviço LIMPA o item escolhido antes. Só
                      // desabilitar o seletor deixava o id viajar junto no
                      // envio, o pedido copiava esse id e o recebimento dava
                      // entrada de material que nunca existiu.
                      patchLine(index, { kind, ...(kind === 'SERVICO' ? { itemId: '' } : {}) });
                    }}
                  >
                    {REQUISITION_ITEM_KINDS.map((value) => (
                      <option key={value} value={value}>
                        {REQUISITION_ITEM_KIND_LABELS[value]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="linhas__celula" data-rotulo="Item do estoque">
                  <select
                    className="input"
                    aria-label={`Item do estoque da linha ${index + 1}`}
                    value={line.itemId}
                    disabled={line.kind === 'SERVICO'}
                    onChange={(event) => {
                      const chosen = (items.data ?? []).find(
                        (item) => item.id === event.target.value,
                      );
                      patchLine(index, {
                        itemId: event.target.value,
                        description: chosen?.name ?? line.description,
                        unit: chosen?.unit ?? line.unit,
                      });
                    }}
                  >
                    <option value="">
                      {line.kind === 'SERVICO' ? 'Serviço não tem item' : 'Item avulso'}
                    </option>
                    {(items.data ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.sku} · {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="linhas__celula" data-rotulo="Descrição">
                  <input
                    className="input"
                    aria-label={`Descrição do item ${index + 1}`}
                    value={line.description}
                    onChange={(event) => patchLine(index, { description: event.target.value })}
                  />
                </div>

                <div className="linhas__celula" data-rotulo="Quantidade">
                  <QtyInput
                    ariaLabel={`Quantidade do item ${index + 1}`}
                    value={line.quantityMilli}
                    unit={line.unit}
                    onChange={(value) => patchLine(index, { quantityMilli: value })}
                  />
                </div>

                <div className="linhas__celula" data-rotulo="Unidade">
                  <select
                    className="input"
                    aria-label={`Unidade do item ${index + 1}`}
                    /* A coluna tem 84px: "KG · Quilograma" não cabe na caixa
                       fechada. O nome por extenso continua acessível ao parar
                       o mouse, em vez de sumir do produto. */
                    title={MEASURE_UNIT_LABELS[line.unit]}
                    value={line.unit}
                    onChange={(event) =>
                      patchLine(index, { unit: event.target.value as MeasureUnit })
                    }
                  >
                    {MEASURE_UNITS.map((value) => (
                      <option key={value} value={value} title={MEASURE_UNIT_LABELS[value]}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="linhas__celula" data-rotulo="Preço estimado">
                  <MoneyInput
                    ariaLabel={`Preço estimado (unitário) do item ${index + 1}`}
                    value={line.estimatedUnitCents}
                    onChange={(value) => patchLine(index, { estimatedUnitCents: value })}
                  />
                </div>

                {/* O total da linha é CALCULADO e não se digita: se ele fosse
                    campo, alguém corrigiria o total sem corrigir o preço e a
                    cotação sairia com número que não fecha. */}
                <div className="linhas__total" data-rotulo="Total">
                  {formatCents(totalDaLinha)}
                </div>

                <div className="linhas__acao">
                  {lines.length > 1 && (
                    <Button
                      small
                      variant="ghost"
                      icon={Trash2}
                      aria-label={`Remover o item ${index + 1}`}
                      onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                    />
                  )}
                </div>
              </div>
            );
          })}

          <dl className="linhas__rodape">
            <div className="linhas__par">
              <dt>Itens</dt>
              <dd>{validLines.length}</dd>
            </div>
            <div className="linhas__par is-total">
              <dt>Estimativa total</dt>
              <dd>{formatCents(total)}</dd>
            </div>
          </dl>
        </div>

        <Button
          icon={Plus}
          small
          style={{ marginTop: 12 }}
          onClick={() => setLines((current) => [...current, LINHA_VAZIA])}
        >
          Adicionar item
        </Button>
      </FormSection>
    </Drawer>
  );
}
